import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Box, Card, CardContent, Typography } from "@mui/material";
import Headeer from "../../components/Headeer";
import StatsFilters from "../../components/Stats/StatsFilters";
import StatsChart from "../../components/Stats/StatsChart";
import StatsSummaryCards from "../../components/Stats/StatsSummaryCards";
import StatsService from "../../services/doctorServices/StatsService";
import FileService from "../../services/doctorServices/FileService";
import { tokens } from "../../theme";
import { useTheme } from "@emotion/react";

const FILES_PAGE_ENTITIES = [
  { value: 'files', label: 'Files' },
  { value: 'forms', label: 'Forms' },
  { value: 'form_invitations', label: 'Form Invitations' },
  { value: 'form_answers', label: 'Form Answers' }
];

const FILES_PAGE_METRICS = {
  files: [
    { value: 'count', label: 'Total Count' },
    { value: 'forms_by_file', label: 'Forms by File' }
  ],
  forms: [
    { value: 'count', label: 'Total Count' },
    { value: 'by_type', label: 'By Type' },
    { value: 'by_file', label: 'By File' },
    { value: 'with_formula', label: 'With Formula' }
  ],
  form_invitations: [
    { value: 'count', label: 'Total Count' },
    { value: 'sent_count', label: 'Sent Count (Daily)' },
    { value: 'sent_count_cumulative', label: 'Total Sent (Cumulative)' },
    { value: 'completed_count', label: 'Completed Count' },
    { value: 'pending_count', label: 'Pending Count' }
  ],
  form_answers: [
    { value: 'count', label: 'Total Count' },
    { value: 'by_form', label: 'By Form' }
  ]
};

function getFilesPageMetricsForEntity(entity) {
  return FILES_PAGE_METRICS[entity] || FILES_PAGE_METRICS.files;
}

export default function FilesStatistics() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('filesPage');
  const fileService = new FileService();
  const statsService = useMemo(() => new StatsService(), []);

  const [files, setFiles] = useState([]);
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const [selectedEntity, setSelectedEntity] = useState('files');
  const [selectedMetric, setSelectedMetric] = useState('count');
  const [selectedTimeFilter, setSelectedTimeFilter] = useState('last_month');
  const [selectedGroupBy, setSelectedGroupBy] = useState('day');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  useEffect(() => {
    const loadFiles = async () => {
      try {
        const res = await fileService.getAllFiles();
        // Backend array döndürüyor; bazen { files: [...] } de olabilir
        const list = Array.isArray(res) ? res : (res?.files || []);
        setFiles(list);
      } catch (e) {
        setFiles([]);
      }
    };
    loadFiles();
  }, []);

  useEffect(() => {
    const firstMetric = getFilesPageMetricsForEntity(selectedEntity)[0]?.value || 'count';
    setSelectedMetric(firstMetric);
  }, [selectedEntity]);

  const fetchStatsData = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      let startDate, endDate;
      if (selectedTimeFilter === 'custom') {
        startDate = customStartDate || '';
        endDate = customEndDate || '';
        if (!startDate || !endDate) {
          setStatsError('Please select both start and end dates for custom date range');
          setStatsLoading(false);
          setStatsData(null);
          return;
        }
        if (new Date(endDate) < new Date(startDate)) {
          setStatsError('End date must be after start date');
          setStatsLoading(false);
          setStatsData(null);
          return;
        }
      } else {
        const range = statsService.getDateRange(selectedTimeFilter);
        startDate = range.startDate;
        endDate = range.endDate;
      }
      const groupBy = selectedGroupBy === 'none'
        ? statsService.getSuggestedGroupBy(selectedTimeFilter)
        : selectedGroupBy;
      const params = {
        entity: selectedEntity,
        metric: selectedMetric,
        start_date: startDate,
        end_date: endDate,
        group_by: groupBy
      };
      const response = await statsService.getDoctorStats(params);
      if (response && response.data) {
        setStatsData(response);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Stats error:', error);
      setStatsError(error.response?.data?.message || error.message || 'Failed to load stats');
      setStatsData(null);
    } finally {
      setStatsLoading(false);
    }
  }, [selectedEntity, selectedMetric, selectedTimeFilter, selectedGroupBy, customStartDate, customEndDate, statsService]);

  useEffect(() => {
    if (selectedTimeFilter !== 'custom' || (customStartDate && customEndDate)) {
      fetchStatsData();
    }
  }, [fetchStatsData, selectedTimeFilter, customStartDate, customEndDate]);

  return (
    <Box sx={{ p: 3 }}>
      <Headeer title={t('statisticsTitle')} subtitle={t('statisticsSubtitle')} />

      <Box sx={{ mb: 4, mt: 3 }}>
        <StatsFilters
          entity={selectedEntity}
          metric={selectedMetric}
          timeFilter={selectedTimeFilter}
          groupBy={selectedGroupBy}
          onEntityChange={setSelectedEntity}
          onMetricChange={setSelectedMetric}
          onTimeFilterChange={(value) => {
            setSelectedTimeFilter(value);
            if (value !== 'custom') {
              setCustomStartDate('');
              setCustomEndDate('');
            }
            setSelectedGroupBy(statsService.getSuggestedGroupBy(value));
          }}
          onGroupByChange={setSelectedGroupBy}
          onRefresh={fetchStatsData}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          onCustomStartDateChange={setCustomStartDate}
          onCustomEndDateChange={setCustomEndDate}
          entities={FILES_PAGE_ENTITIES}
          getMetricsForEntity={getFilesPageMetricsForEntity}
        />
        {statsData?.data?.[selectedEntity]?.summary && (
          <StatsSummaryCards
            summary={statsData.data[selectedEntity].summary}
            comparison={statsData.data[selectedEntity].comparison}
            entity={selectedEntity}
            metric={selectedMetric}
          />
        )}
        <Card
          sx={{
            background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
            borderRadius: '16px',
            border: `1px solid ${colors.grey[800]}30`,
            mt: 2,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: colors.grey[100] }}>
              {selectedEntity.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())} - {selectedMetric.replace(/_/g, ' ').toUpperCase()}
            </Typography>
            <StatsChart
              data={statsData?.data}
              loading={statsLoading}
              error={statsError}
              metric={selectedMetric}
              entity={selectedEntity}
              files={files}
              period={selectedTimeFilter}
              groupBy={selectedGroupBy === 'none' ? statsService.getSuggestedGroupBy(selectedTimeFilter) : selectedGroupBy}
            />
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
